import { themeVars } from '@instamenu/ui-vanilla-extract';
import { Box } from '../box/box';
import { Text } from '../text/text';
import { Button } from '../button/button';
import { Card } from '../card/card';
import { ModalNext, ModalNextProps } from '../modal/modal';
import { RadioButton } from '../radio-button/radio-button';
import { Stack } from '../stack/stack';
import {
  scrollableBox,
  selectionModalContainer,
  selectionModalFooter,
  selectionModalText,
} from './selection-modal.css';

type WithIdentifier = {
  id?: string | number;
  key?: string | number;
};

type ModalSelectProps<T extends WithIdentifier> = {
  options: T[];
  value: T | string | number | null;
  onChange: (value: T) => void;
  renderItem: (item: T) => React.ReactNode;
  title: string;
  onAddNew?: () => void;
  addNewButtonText?: string;
  confirmButtonText?: string;
  onClose: () => void;
  renderActions?: (item: T) => React.ReactNode;
  onConfirm?: () => void;
  radioRight?: boolean;
} & Omit<ModalNextProps, 'onChange'>;

export const SelectionModal = <T extends WithIdentifier>({
  options,
  value,
  onChange,
  renderItem,
  renderActions,
  title,
  onAddNew,
  addNewButtonText,
  confirmButtonText = 'Confirm',
  onClose,
  onConfirm,
  radioRight,
  opened,
  ...rest
}: ModalSelectProps<T>) => {
  const getId = (item: T) => item.id ?? item.key;

  const isSelected = (item: T) => {
    if (value === null) return false;
    if (typeof value === 'object') {
      return getId(item) === getId(value);
    }
    return getId(item) === value;
  };
  return (
    <ModalNext onRequestClose={onClose} opened={opened} {...rest}>
      <Stack className={selectionModalContainer}>
        <Text className={selectionModalText}>{title}</Text>
        <Box className={scrollableBox}>
          {options.map((item) => (
            <Card
              key={getId(item)}
              css={{
                margin: '8px 0',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: isSelected(item)
                  ? `1px solid ${themeVars.color.brand}`
                  : '1px solid rgba(0, 0, 0, 0.07)',
              }}
            >
              <Box
                onClick={() => onChange(item)}
                css={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  cursor: 'pointer',
                  width: '100%',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                {!radioRight && <RadioButton checked={isSelected(item)} name="modal-select" />}
                {renderItem(item)}
                {radioRight && <RadioButton checked={isSelected(item)} name="modal-select" />}
              </Box>
              {renderActions?.(item)}
            </Card>
          ))}
        </Box>
        <Stack className={selectionModalFooter}>
          {addNewButtonText && onAddNew && (
            <Button variant="text" onClick={onAddNew} css={{ alignSelf: 'flex-start' }}>
              {addNewButtonText}
            </Button>
          )}
          <Button variant="primary" onClick={onConfirm}>
            {confirmButtonText}
          </Button>
        </Stack>
      </Stack>
    </ModalNext>
  );
};
